/**
 * EtEindresultaat — het resultaatrapport ná inleveren (Facet-blauwdruk §10 punt 2).
 *
 * Zoals op het echte examen: een kop met de belangrijkste gegevens en daaronder
 * per opgave een tabel met per vraag de maximale en behaalde score. Elk
 * vraagnummer is klikbaar → terugblik op die vraag (punt 3). Eigen opmaak.
 */
import React from 'react';
import { QvButton } from '../../pages/QuestionView';
import './EtEindresultaat.css';

/**
 * @param {object} p
 * @param {string} p.examNaam
 * @param {string} p.deelnemer
 * @param {Array<{nummer:string, titel:string, vragen:Array<{index:number, vraagnummer:string, max:number, score:number|null}>}>} p.opgaven
 * @param {number} p.totaal  behaalde punten (alleen beoordeelde)
 * @param {number} p.max     maximaal haalbare punten
 * @param {number|null} p.cijfer  omzettingstabel van CvTE; null als het niet eerlijk kan
 * @param {number|null} p.nTerm   de N-term die bij dat examen hoorde
 * @param {(index:number)=>void} p.onNaarVraag
 * @param {()=>void} p.onOpnieuw
 */
export default function EtEindresultaat({ examNaam, deelnemer, opgaven, totaal, max,
  cijfer = null, nTerm = null, onNaarVraag, onOpnieuw }) {
  const datum = new Date().toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' });
  const komma = (n) => String(n.toFixed(1)).replace('.', ',');
  return (
    <div className="ete-wrap">
      <section className="ete-kop">
        <h2>Jouw resultaat</h2>
        <dl>
          <div><dt>Training</dt><dd>{examNaam}</dd></div>
          <div><dt>Naam</dt><dd>{deelnemer}</dd></div>
          <div><dt>Gemaakt op</dt><dd>{datum}</dd></div>
          <div className="ete-score-rij"><dt>Jouw punten</dt><dd><b>{totaal}</b> van de {max}</dd></div>
          {cijfer !== null && (
            <div className="ete-cijfer-rij">
              <dt>Cijfer</dt>
              <dd>
                <b>{komma(cijfer)}</b>
                <span className="ete-cijfer-uitleg">
                  volgens de omzettingstabel van het echte examen
                  {nTerm !== null ? `, N-term ${komma(nTerm)}` : ''}
                </span>
              </dd>
            </div>
          )}
        </dl>
        {cijfer === null && (
          <p className="ete-geen-cijfer">
            Bij dit onderdeel hoort geen examencijfer: de omzettingstabel van CvTE
            geldt voor het hele examen, en je maakte er een deel van.
          </p>
        )}
      </section>

      <section className="ete-tabellen">
        <h3>Per vraag</h3>
        <p>Klik op een vraagnummer om je antwoord en het antwoordmodel terug te zien.</p>
        {opgaven.map((o) => (
          <div key={o.nummer} className="ete-opgave">
            <h4>{o.nummer}{o.titel ? ` — ${o.titel}` : ''}</h4>
            <table>
              <thead><tr><th scope="col">Vraag</th><th scope="col">Punten te halen</th><th scope="col">Jouw punten</th></tr></thead>
              <tbody>
                {o.vragen.map((v) => (
                  <tr key={v.vraagnummer}>
                    <td>
                      <button className="ete-vraaglink" onClick={() => onNaarVraag(v.index)}
                        aria-label={`Bekijk vraag ${v.vraagnummer} terug`}>{v.vraagnummer}</button>
                    </td>
                    <td>{v.max}</td>
                    <td>{v.score === null ? <span className="ete-nvt">niet nagekeken</span> : v.score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </section>

      <div className="ete-onder">
        <QvButton variant="primary" onClick={onOpnieuw} ariaLabel="Oefen deze opgave opnieuw">
          Opnieuw oefenen
        </QvButton>
      </div>
    </div>
  );
}
